/**
 * features/finance/AuditPack.tsx
 * ------------------------------------------------------------------
 * Choose a period. Get everything an auditor asks for, in one archive.
 *
 * An audit is a list of questions the school already has answers to,
 * asked one at a time over three weeks — trial balance, then the ledger
 * behind a figure on it, then the reconciliation proving that ledger
 * agrees with the detail. Every request is somebody finding a report,
 * exporting it and emailing it, and most of the three weeks is waiting.
 *
 * THIS FILE COMPUTES NOTHING. It gathers the live data, hands it to
 * lib/auditPack, and writes what comes back. Every figure is produced by
 * the same module the corresponding screen uses, so the pack cannot
 * disagree with what the bursar sees.
 *
 * BOTH FORMATS, ALWAYS. The CSV carries every row; the PDF carries the
 * first few hundred of a long section and says so. A general ledger for a
 * school year is ten thousand lines — rendering that as a PDF is two
 * hundred pages nobody opens and a browser tab that looks hung.
 *
 * THE MANIFEST IS IN THE ZIP, and it lists what was deliberately left
 * out. An auditor who discovers a missing section themselves stops
 * trusting the sections that are there.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, query } from 'firebase/firestore';
import {
  Archive, Download, Loader2, ShieldCheck, TriangleAlert, CheckCircle2,
} from 'lucide-react';
import { Spinner } from '@/components/ui/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { db } from '@/lib/firebase';
import { SCHOOL } from '@/lib/school.config';
import { FINANCE_COLLECTIONS } from '@/lib/financeCore';
import { useGlData } from '@/features/finance/useGlData';
import { useFinanceData } from '@/features/finance/useFinanceData';
import { buildTrialBalance } from '@/lib/glReport';
import { buildIncomeStatement, buildBalanceSheet } from '@/lib/financialStatements';
import { buildControlReport } from '@/lib/controlAccounts';
import { buildDebtors } from '@/lib/debtors';
import {
  VOUCHER_COLLECTIONS, creditorsBySupplier, type Voucher,
} from '@/lib/vouchers';
import {
  PETTY_COLLECTIONS, floatState,
  type PettyFloat, type PettySlip, type PettyCount,
} from '@/lib/pettyCash';
import { VAT_OFF, vatActive, buildVatReturn, type VatSettings } from '@/lib/vat';
import { buildAuditPack, packManifest, type PackSources } from '@/lib/auditPack';
import { buildTablePdf, toCsv, type TableExport } from '@/lib/tableExport';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, glassFieldClass, btnPrimary,
} from '@/features/finance/ui';

/**
 * How many rows of a section reach its PDF.
 *
 * The CSV is always complete. This only caps the readable copy, and the
 * cap is stated on the page rather than silently truncating.
 */
const PDF_ROW_CAP = 400;

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AuditPack() {
  const { userData } = useAuth();
  const { students, families, classes } = useData();
  const fin = useFinanceData();

  const thisYear = new Date().getFullYear();
  const [from, setFrom] = useState(`${thisYear}-01-01`);
  const [to, setTo] = useState(`${thisYear}-12-31`);
  const [building, setBuilding] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  const gl = useGlData(`FY${from.slice(0, 4)}`);

  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [floats, setFloats] = useState<PettyFloat[]>([]);
  const [slips, setSlips] = useState<PettySlip[]>([]);
  const [counts, setCounts] = useState<PettyCount[]>([]);
  const [vat, setVat] = useState<VatSettings>(VAT_OFF);

  useEffect(() => onSnapshot(query(collection(db, VOUCHER_COLLECTIONS.vouchers)),
    (s) => setVouchers(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Voucher)),
    () => setVouchers([])), []);
  useEffect(() => onSnapshot(query(collection(db, PETTY_COLLECTIONS.floats)),
    (s) => setFloats(s.docs.map((d) => ({ id: d.id, ...d.data() }) as PettyFloat)),
    () => setFloats([])), []);
  useEffect(() => onSnapshot(query(collection(db, PETTY_COLLECTIONS.slips)),
    (s) => setSlips(s.docs.map((d) => ({ id: d.id, ...d.data() }) as PettySlip)),
    () => setSlips([])), []);
  useEffect(() => onSnapshot(query(collection(db, PETTY_COLLECTIONS.counts)),
    (s) => setCounts(s.docs.map((d) => ({ id: d.id, ...d.data() }) as PettyCount)),
    () => setCounts([])), []);
  useEffect(() => onSnapshot(doc(db, FINANCE_COLLECTIONS.settings, 'school'),
    (s) => setVat((s.data()?.vat ?? VAT_OFF) as VatSettings),
    () => setVat(VAT_OFF)), []);

  const sources: PackSources | null = useMemo(() => {
    if (gl.loading || fin.loading) return null;

    const range = { from, to };
    const trialBalance = buildTrialBalance(gl.accounts, gl.journals, range);

    const ledgerInService = Boolean(
      fin.settings.glCutoverDate && fin.settings.glPostingEnabled,
    );

    return {
      period: { from, to, label: `${from} to ${to}` },
      trialBalance,
      income: buildIncomeStatement(trialBalance),
      balanceSheet: buildBalanceSheet(trialBalance),
      control: buildControlReport({
        accounts: gl.accounts,
        journals: gl.journals,
        invoices: fin.invoices,
        vouchers,
        ledgerInService,
      }),
      debtors: buildDebtors({
        invoices: fin.invoices,
        payments: fin.payments,
        students,
        families,
        classNameOf: (id) => classes.find((c) => c.id === id)?.name,
        thresholds: fin.settings.agingThresholds,
      }),
      creditors: creditorsBySupplier(vouchers),
      vat: vatActive(vat)
        ? buildVatReturn(vat, gl.journals, gl.accounts, from, to)
        : null,
      pettyFloats: floats.map((f) => {
        const mine = slips.filter((s) => s.floatId === f.id);
        const myCounts = counts.filter((c) => c.floatId === f.id);
        return {
          name: f.name,
          custodianName: f.custodianName,
          state: floatState(f, mine, myCounts),
          counts: myCounts,
        };
      }),
      /* Deliberately absent: the anomaly review runs on its own screen
         against the whole dataset, not a period, and forcing it into one
         here would report a different answer from the screen. */
    };
  }, [gl, fin, from, to, vouchers, floats, slips, counts, vat, students, families, classes]);

  const sections = useMemo(() => (sources ? buildAuditPack(sources) : []), [sources]);

  async function download() {
    if (!sources) return;
    setBuilding(true);
    setDone(null);
    try {
      /* Loaded on demand, the way BulkPayslips and AttendanceRegisters
         already load it. A static import pulls the whole zip library into
         the main bundle for every user who never opens this screen. */
      const { default: JSZip } = await import('jszip');
      const zip = new JSZip();

      zip.file('00-MANIFEST.txt', packManifest(
        sources, sections, SCHOOL.name, userData?.displayName,
      ));

      sections.forEach((s, i) => {
        const n = String(i + 1).padStart(2, '0');
        const capped = s.rows.length > PDF_ROW_CAP;

        const spec: TableExport<never> = {
          title: s.title.toUpperCase(),
          meta: `${sources.period.label} · ${s.purpose}`,
          columns: s.columns,
          rows: (capped ? s.rows.slice(0, PDF_ROW_CAP) : s.rows) as never[],
          totals: s.totals,
          note: [
            s.note,
            capped
              ? `Showing the first ${PDF_ROW_CAP} of ${s.rows.length} rows. `
                + 'The CSV beside this file carries all of them.'
              : '',
          ].filter(Boolean).join(' '),
        };

        zip.file(`${n}-${s.key}.pdf`, buildTablePdf(spec).output('blob'));
        /* The CSV is never capped. A PDF is for reading; a CSV is what an
           auditor actually works in. */
        zip.file(`${n}-${s.key}.csv`, toCsv(s.columns, s.rows as never[]));
      });

      const blob = await zip.generateAsync({ type: 'blob' });
      const name = `audit-pack-${from}-to-${to}.zip`;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = name;
      a.click();
      URL.revokeObjectURL(a.href);
      setDone(name);
    } finally {
      setBuilding(false);
    }
  }

  if (!sources) return <div className="p-6 flex justify-center"><Spinner /></div>;

  const tb = sources.trialBalance;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <PageHeader
        icon={Archive} tone="navy"
        title="Audit Pack"
        subtitle="A period, one action, everything an auditor will ask for"
        actions={(
          <button type="button" className={btnPrimary} onClick={download} disabled={building}>
            {building ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
            {building ? 'Building…' : 'Download the pack'}
          </button>
        )}
      />

      <SoftCard className="mb-5">
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-gray-400">From</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className={glassFieldClass('mt-1')} />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-gray-400">To</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className={glassFieldClass('mt-1')} />
          </label>
          {done && (
            <span className="text-[13px] text-emerald-700 dark:text-emerald-400 flex items-center gap-1 pb-2">
              <CheckCircle2 size={14} /> {done}
            </span>
          )}
        </div>
      </SoftCard>

      {/* The one thing worth knowing before the pack leaves the building. */}
      {!tb.balanced && (
        <SoftCard className="mb-5 border-l-4 border-red-400">
          <p className="text-sm text-red-700 dark:text-red-300">
            <TriangleAlert size={15} className="inline mr-1 -mt-0.5" />
            The trial balance does not balance for this period — debits exceed credits by{' '}
            <strong>{money(tb.difference)}</strong>. Every journal is checked before it is
            written, so this means one bypassed that check or lines are missing. Everything
            in the pack rests on it.
          </p>
        </SoftCard>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <StatChip tone="navy" icon={Archive} value={String(sections.length)} label="Sections" />
        <StatChip tone="slate"
          value={String(sections.reduce((n, s) => n + s.rows.length, 0))} label="Rows" />
        <StatChip tone={tb.balanced ? 'emerald' : 'rose'}
          icon={tb.balanced ? ShieldCheck : TriangleAlert}
          value={tb.balanced ? 'Balances' : money(tb.difference)} label="Trial balance" />
      </div>

      <SoftCard pad="p-0">
        <PanelHeader icon={Archive} tone="navy" title="What is in the pack"
          subtitle="Each section as a PDF to read and a CSV to work in" />
        <div className="divide-y divide-gray-50 dark:divide-gray-800">
          {sections.map((s) => (
            <div key={s.key} className="px-4 py-3">
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-medium text-gray-900 dark:text-white">{s.title}</span>
                <span className="text-[12px] text-gray-400 tabular-nums shrink-0">
                  {s.rows.length} row{s.rows.length === 1 ? '' : 's'}
                  {s.rows.length > PDF_ROW_CAP && ' · PDF abridged'}
                </span>
              </div>
              <p className="text-[12px] text-gray-500 mt-0.5">{s.purpose}</p>
              {s.note && (
                <p className="text-[12px] text-amber-700 dark:text-amber-300 mt-1">{s.note}</p>
              )}
            </div>
          ))}
        </div>
        <p className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 text-[12px] text-gray-500">
          The archive also carries a manifest naming the period, how each figure was derived,
          and every section deliberately left out. Nothing here is recomputed for the pack —
          each figure comes from the same module the corresponding screen uses.
        </p>
      </SoftCard>
    </div>
  );
}
